import React from 'react';

type IconProps = { className?: string };

/**
 * Q1 demand-level gauge.
 * 5 needle positions from very low (1) to constant pressure (5).
 * Color follows `currentColor` so it can be themed via Tailwind text color.
 */
export function GaugeIcon({ level, className }: IconProps & { level: 1 | 2 | 3 | 4 | 5 }) {
  const needlePaths: Record<1 | 2 | 3 | 4 | 5, string> = {
    1: 'M 4 18 A 12 12 0 0 1 7.6 9.6',
    2: 'M 4 18 A 12 12 0 0 1 11.6 7.4',
    3: 'M 4 18 A 12 12 0 0 1 16 6',
    4: 'M 4 18 A 12 12 0 0 1 20.4 7.4',
    5: 'M 4 18 A 12 12 0 0 1 28 18',
  };
  const needleLines: Record<1 | 2 | 3 | 4 | 5, { x2: number; y2: number }> = {
    1: { x2: 6.5, y2: 11 },
    2: { x2: 9.5, y2: 8.5 },
    3: { x2: 16, y2: 7.5 },
    4: { x2: 22.5, y2: 8.5 },
    5: { x2: 25.5, y2: 11 },
  };
  return (
    <svg viewBox="0 0 32 22" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M 4 18 A 12 12 0 0 1 28 18"
        stroke="#E5E7EB"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d={needlePaths[level]}
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
      <line
        x1="16"
        y1="18"
        x2={needleLines[level].x2}
        y2={needleLines[level].y2}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="16" cy="18" r="1.5" fill="currentColor" />
    </svg>
  );
}

/**
 * Q2 self-assessment face.
 * 1 = comfortable smile, 5 = overwhelmed (X eyes + pained mouth).
 */
export function FaceIcon({ level, className }: IconProps & { level: 1 | 2 | 3 | 4 | 5 }) {
  // Mouth path varies; eyes vary only on level 5.
  const mouths: Record<1 | 2 | 3 | 4 | 5, React.ReactNode> = {
    1: <path d="M8 14.5 Q12 17.5 16 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />,
    2: <path d="M8.5 15 Q12 16.5 15.5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />,
    3: <line x1="8.5" y1="15.5" x2="15.5" y2="15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />,
    4: <path d="M8.5 16.5 Q12 15 15.5 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />,
    5: <path d="M8.5 17 Q12 14 15.5 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />,
  };
  const eyes =
    level === 5 ? (
      <>
        <path d="M7.5 9 L10.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M10.5 9 L7.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M13.5 9 L16.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M16.5 9 L13.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </>
    ) : (
      <>
        <circle cx="9" cy="10" r="0.9" fill="currentColor" />
        <circle cx="15" cy="10" r="0.9" fill="currentColor" />
      </>
    );
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className} fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" fill="transparent" />
      {eyes}
      {mouths[level]}
    </svg>
  );
}
